import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  ArrowLeft,
  AlertCircle,
  Clock,
  CheckCircle,
  MessageSquare,
  Filter,
  Search,
  RefreshCw,
  User,
  Mail,
  Calendar,
  Tag,
  Send,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

console.log('AdminSupportDashboard component loaded');

// GraphQL queries and mutations
const GET_ALL_TICKETS = gql`
  query GetSupportTickets($status: String, $category: String) {
    supportTickets(status: $status, category: $category) {
      id
      userId
      email
      category
      message
      status
      priority
      responses {
        message
        isStaff
        createdAt
      }
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_TICKET_STATUS = gql`
  mutation UpdateTicketStatus($id: ID!, $status: String!) {
    updateTicketStatus(id: $id, status: $status) {
      id
      status
    }
  }
`;

const ADMIN_CREATE_TICKET = gql`
  mutation AdminCreateTicket($input: AdminTicketInput!) {
    adminCreateTicket(input: $input) {
      id
      email
      category
      message
      status
      priority
    }
  }
`;

const UPDATE_TICKET_PRIORITY = gql`
  mutation UpdateTicketPriority($id: ID!, $priority: String!) {
    updateTicketPriority(id: $id, priority: $priority) {
      id
      priority
    }
  }
`;

const ASSIGN_TICKET = gql`
  mutation AssignTicket($id: ID!, $staffUserId: String!) {
    assignTicket(id: $id, staffUserId: $staffUserId) {
      id
      assignedTo {
        id
        email
      }
    }
  }
`;


const RESPOND_TO_TICKET = gql`
  mutation RespondToTicket($id: ID!, $message: String!, $isStaff: Boolean!) {
    respondToTicket(id: $id, message: $message, isStaff: $isStaff) {
      id
      responses {
        message
        isStaff
        createdAt
      }
    }
  }
`;

const GET_ADMIN_USERS = gql`
  query GetAdminUsers {
    users(role: "ADMIN") {
      id
      email
      name
    }
  }
`;

const AdminSupportDashboard = () => {
    console.log('AdminSupportDashboard rendering');
  const navigate = useNavigate();
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [expandedTickets, setExpandedTickets] = useState(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTicket, setNewTicket] = useState({ email: '', category: 'other', message: '', priority: 'MEDIUM' });
  const { data: adminData } = useQuery(GET_ADMIN_USERS);
  const adminUsers = adminData?.users || [];

  // Check if user is admin
  useEffect(() => {
    const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
    if (adminUser.role !== 'ADMIN') {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const { loading, error, data, refetch } = useQuery(GET_ALL_TICKETS, {
    variables: {
      status: filterStatus !== 'ALL' ? filterStatus : undefined,
      category: filterCategory !== 'ALL' ? filterCategory : undefined
    },
    pollInterval: 30000 // Refresh every 30 seconds
  });

  const [updateStatus] = useMutation(UPDATE_TICKET_STATUS, {
    onCompleted: () => refetch()
  });

  const [respondToTicket] = useMutation(RESPOND_TO_TICKET, {
    onCompleted: () => {
      setResponseMessage('');
      refetch();
    }
  });

  const [adminCreateTicket] = useMutation(ADMIN_CREATE_TICKET, {
    onCompleted: () => {
      setShowCreateModal(false);
      setNewTicket({ email: '', category: 'other', message: '', priority: 'MEDIUM' });
      refetch();
    }
  });

  const [updatePriority] = useMutation(UPDATE_TICKET_PRIORITY, {
    onCompleted: () => refetch()
  });

  const [assignTicket] = useMutation(ASSIGN_TICKET, {
    onCompleted: () => refetch()
  });

  const tickets = data?.supportTickets || [];

  // Filter tickets based on search
  const filteredTickets = tickets.filter(ticket => 
    ticket.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.id.includes(searchTerm)
  );

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await updateStatus({
        variables: { id: ticketId, status: newStatus }
      });
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleResponse = async (ticketId) => {
    if (!responseMessage.trim()) return;

    try {
      await respondToTicket({
        variables: {
          id: ticketId,
          message: responseMessage,
          isStaff: true
        }
      });
    } catch (err) {
      console.error('Error responding:', err);
    }
  };

  const toggleTicketExpansion = (ticketId) => {
    const newExpanded = new Set(expandedTickets);
    if (newExpanded.has(ticketId)) {
      newExpanded.delete(ticketId);
    } else {
      newExpanded.add(ticketId);
    }
    setExpandedTickets(newExpanded);
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'URGENT': return 'bg-red-100 text-red-800 border-red-200';
      case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'LOW': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'OPEN': return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'RESOLVED': return 'bg-green-100 text-green-800';
      case 'CLOSED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'order_issue': return '📦';
      case 'delivery_problem': return '🚚';
      case 'payment_issue': return '💳';
      case 'product_quality': return '⭐';
      case 'group_question': return '👥';
      case 'account_help': return '👤';
      default: return '❓';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Stats calculation
  const stats = {
    open: tickets.filter(t => t.status === 'OPEN').length,
    inProgress: tickets.filter(t => t.status === 'IN_PROGRESS').length,
    resolved: tickets.filter(t => t.status === 'RESOLVED').length,
    urgent: tickets.filter(t => t.priority === 'URGENT' || t.priority === 'HIGH').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <p className="text-red-600">Error loading tickets</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <button 
              onClick={() => navigate(-1)}
              className="mr-3 p-2 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Support Ticket Management</h1>
              <p className="text-sm text-gray-600">Admin Dashboard</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 mr-2"
          >
            + Create Ticket
          </button>
          <button
            onClick={() => refetch()}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white px-6 py-4 border-b">
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.open}</div>
            <div className="text-sm text-gray-600">Open</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">{stats.inProgress}</div>
            <div className="text-sm text-gray-600">In Progress</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{stats.resolved}</div>
            <div className="text-sm text-gray-600">Resolved</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{stats.urgent}</div>
            <div className="text-sm text-gray-600">High Priority</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white px-6 py-4 border-b">
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by email, message, or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="ALL">All Status</option>
            <option value="OPEN">Open</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="RESOLVED">Resolved</option>
            <option value="CLOSED">Closed</option>
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="ALL">All Categories</option>
            <option value="order_issue">Order Issue</option>
            <option value="delivery_problem">Delivery Problem</option>
            <option value="payment_issue">Payment Issue</option>
            <option value="product_quality">Product Quality</option>
            <option value="group_question">Group Question</option>
            <option value="account_help">Account Help</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      {/* Tickets List */}
      <div className="p-6">
        {filteredTickets.length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center">
            <MessageSquare size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">No tickets found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredTickets.map((ticket) => {
              const isExpanded = expandedTickets.has(ticket.id);
              
              return (
                <div key={ticket.id} className="bg-white rounded-lg shadow-sm">
                  {/* Ticket Header */}
                  <div className="p-4 border-b">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">{getCategoryIcon(ticket.category)}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                            {ticket.priority}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                            {ticket.status}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Mail size={14} />
                            {ticket.email}
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar size={14} />
                            {formatDate(ticket.createdAt)}
                          </div>
                          <div className="flex items-center gap-1">
                            <Tag size={14} />
                            {ticket.id.slice(-6).toUpperCase()}
                          </div>
                        </div>
                        
                        <p className="mt-2 text-gray-800">{ticket.message}</p>
                      </div>
                      
                      <button
                        onClick={() => toggleTicketExpansion(ticket.id)}
                        className="p-2 hover:bg-gray-100 rounded"
                      >
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="p-4">
                      {/* Status Update */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">Update Status</label>
                        <div className="flex gap-2">
                          {['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'].map(status => (
                            <button
                              key={status}
                              onClick={() => handleStatusChange(ticket.id, status)}
                              className={`px-3 py-1 rounded text-sm ${
                                ticket.status === status
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 hover:bg-gray-200'
                              }`}
                            >
                              {status.replace('_', ' ')}
                            </button>
                          ))}
                        </div>
                        {/* Priority Update */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium mb-2">Update Priority</label>
                          <div className="flex gap-2">
                            {['LOW', 'MEDIUM', 'HIGH', 'URGENT'].map(priority => (
                              <button
                                key={priority}
                                onClick={() => updatePriority({ variables: { id: ticket.id, priority } })}
                                className={`px-3 py-1 rounded text-sm ${
                                  ticket.priority === priority
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 hover:bg-gray-200'
                                }`}
                              >
                                {priority}
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Assign to Staff */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium mb-2">Assign to Staff</label>
                          <select
                            value={ticket.assignedTo?.id || ''}
                            onChange={(e) => {
                              if (e.target.value) {
                                assignTicket({ variables: { id: ticket.id, staffUserId: e.target.value } });
                              }
                            }}
                            className="px-3 py-2 border rounded-lg"
                          >
                            <option value="">Unassigned</option>
                            {adminUsers.map(admin => (
                              <option key={admin.id} value={admin.id}>
                                {admin.name || admin.email}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Responses */}
                      {ticket.responses && ticket.responses.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-medium mb-2">Conversation History</h4>
                          <div className="space-y-2">
                            {ticket.responses.map((response, idx) => (
                              <div
                                key={idx}
                                className={`p-3 rounded-lg ${
                                  response.isStaff
                                    ? 'bg-blue-50 ml-8'
                                    : 'bg-gray-50 mr-8'
                                }`}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <User size={14} />
                                  <span className="text-xs font-medium">
                                    {response.isStaff ? 'Support Team' : 'Customer'}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {formatDate(response.createdAt)}
                                  </span>
                                </div>
                                <p className="text-sm">{response.message}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Response Form */}
                      <div className="border-t pt-4">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={responseMessage}
                            onChange={(e) => setResponseMessage(e.target.value)}
                            placeholder="Type your response..."
                            className="flex-1 px-3 py-2 border rounded-lg"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleResponse(ticket.id);
                              }
                            }}
                          />
                          <button
                            onClick={() => handleResponse(ticket.id)}
                            disabled={!responseMessage.trim()}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 flex items-center gap-2"
                          >
                            <Send size={16} />
                            Send
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Create Support Ticket</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={newTicket.email}
                  onChange={(e) => setNewTicket({...newTicket, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="customer@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  value={newTicket.category}
                  onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="order_issue">Order Issue</option>
                  <option value="delivery_problem">Delivery Problem</option>
                  <option value="payment_issue">Payment Issue</option>
                  <option value="product_quality">Product Quality</option>
                  <option value="group_question">Group Question</option>
                  <option value="account_help">Account Help</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Priority</label>
                <select
                  value={newTicket.priority}
                  onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="URGENT">Urgent</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Message</label>
                <textarea
                  value={newTicket.message}
                  onChange={(e) => setNewTicket({...newTicket, message: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={4}
                  placeholder="Describe the issue..."
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => adminCreateTicket({ variables: { input: newTicket } })}
                disabled={!newTicket.email || !newTicket.message}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
              >
                Create Ticket
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default AdminSupportDashboard;